import { Navigate, Route, useLocation } from 'react-router-dom';
import {
  EntitlementRouteGuard,
} from '../../features/entitlements/customerEntitlementGuards.js';
import {
  ensureTenantInSearch,
  PLATFORM_SIGN_UP_PATH,
} from '../../features/auth/customerAuthPaths.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { CardLinkRoute, PickupVerifyPage } from './lazyRouteComponents.js';
import { withRegisteredScreen } from './registryGuard.js';
import {
  LegacyConfirmEmailRedirect,
  SignInRoute,
  Wave1Suspense,
} from './routeHelpers.js';

/**
 * Non-login auth under `/{tenant}/…` → platform chrome.
 * Register / pending / forgot / reset may inject `tenant=<pathSlug>` as **visit**
 * tenant (register-only). Login never uses this helper — see first-class `sign-in`.
 */
function TenantAuthPlatformRedirect({
  platformPath,
}: {
  platformPath: string;
}): JSX.Element {
  const location = useLocation();
  const pathSlug = useTenantCode();
  const search = ensureTenantInSearch(location.search, pathSlug);
  return (
    <Navigate
      to={`${platformPath}${search}${location.hash}`}
      replace
    />
  );
}

export function TenantAuthRoutes(): JSX.Element {
  return (
    <>
      {/* G3 — path-tenant login is first-class; never hard-redirect to /sign-in?tenant= */}
      <Route path="sign-in" element={<SignInRoute />} />
      <Route
        path="sign-up"
        element={<TenantAuthPlatformRedirect platformPath={PLATFORM_SIGN_UP_PATH} />}
      />
      <Route
        path="sign-up/pending"
        element={<TenantAuthPlatformRedirect platformPath="/sign-up/pending" />}
      />
      <Route path="sign-up/confirm-email" element={<LegacyConfirmEmailRedirect />} />
      <Route
        path="confirm-email"
        element={<TenantAuthPlatformRedirect platformPath="/confirm-email" />}
      />
      <Route
        path="onboarding/set-credentials"
        element={
          <TenantAuthPlatformRedirect platformPath="/onboarding/set-credentials" />
        }
      />
      <Route
        path="forgot-password"
        element={<TenantAuthPlatformRedirect platformPath="/forgot-password" />}
      />
      <Route
        path="reset-password"
        element={<TenantAuthPlatformRedirect platformPath="/reset-password" />}
      />
      <Route
        path="card/link"
        element={withRegisteredScreen(
          'MS-AUTH-017',
          <EntitlementRouteGuard blockKeys={['customer_auth_pwa']}>
            <Wave1Suspense>
              <CardLinkRoute />
            </Wave1Suspense>
          </EntitlementRouteGuard>,
        )}
      />
      <Route
        path="pickup/verify"
        element={withRegisteredScreen(
          // MS-PICK-040 = PickupVerifyPage (masterScreenEntries); not MS-PICK-041 (Find Order).
          'MS-PICK-040',
          <EntitlementRouteGuard blockKeys={['order_pickup_infrastructure']}>
            <Wave1Suspense>
              <PickupVerifyPage />
            </Wave1Suspense>
          </EntitlementRouteGuard>,
        )}
      />
    </>
  );
}
